import {
  ProductVariantInfo,
  ProductVariantSneakersResponse,
} from 'productTemplate/types/ProductVariant'
import {
  SizePickerVariantOption,
  convertSizePickerVariantPricing,
  getHighestMarketPricedVariant,
  getHighestPricedVariant,
  getInstantShipPrice,
  getLowestMarketPricedVariant,
  getLowestPricedVariant,
  getMarketPrice,
  getPriceFromVariant,
  isBadBox,
  resolveVariantPrice,
} from 'server/utils/productTemplate/productVariantUtils'
import {
  resolveNewSizes,
  resolveNewSizesInstantShipOnly,
} from 'server/utils/productTemplate/resolveNewSizes'

type GetProductVariantInfoParams = {
  disambiguatedCurrencySymbol: string
  hideHighPricedUsedProduct: boolean
  marketPricingEnabled: boolean
  productTemplateId: string
  productVariants: ProductVariantSneakersResponse[]
}

/**
 * Takes product variants for a given PT and creates variables based off their information.
 * This utility function helps transform the Sneakers API variants response into a more usable format
 * for the PDP.
 */
export const getProductVariantInfo = ({
  disambiguatedCurrencySymbol,
  hideHighPricedUsedProduct,
  marketPricingEnabled,
  productTemplateId,
  productVariants = [],
}: GetProductVariantInfoParams): ProductVariantInfo => {
  const getCheapVariant = marketPricingEnabled
    ? getLowestMarketPricedVariant
    : getLowestPricedVariant
  const getExpensiveVariant = marketPricingEnabled
    ? getHighestMarketPricedVariant
    : getHighestPricedVariant
  const getPrice = marketPricingEnabled ? getMarketPrice : getInstantShipPrice

  // sizeOptionHash has this initial data structure for O(1) lookups
  // while looping through variants. We convert to an array and sort
  // by size before returning sizePickerOptions
  const sizeOptionHash: Record<string, SizePickerVariantOption> = {}

  // helper function that adds the variant to the sizeOptionHash
  const addVariantToSizeOptionHash = (
    variant: ProductVariantSneakersResponse,
    sizeOptionHash: Record<string, SizePickerVariantOption>,
  ) => {
    const variantSize = variant.sizeOption.presentation
    if (!sizeOptionHash[variantSize]) {
      sizeOptionHash[variantSize] = {
        size: {
          display: variant.sizeOption.presentation,
          value: variant.sizeOption.value,
        },
      }
    }
  }

  const newVariants: ProductVariantSneakersResponse[] = []
  let newHighestVariant: ProductVariantSneakersResponse | undefined
  let variant: ProductVariantSneakersResponse
  let variantSize: string

  for (let i = 0, l = productVariants.length; i < l; i += 1) {
    variant = productVariants[i]
    variantSize = variant.sizeOption.presentation
    const hasInstantShip =
      variant.instantShipLowestPriceCents && variant.instantShipLowestPriceCents.amount
    if (!marketPricingEnabled && !hasInstantShip) {
      continue
    }

    switch (variant.shoeCondition) {
      case 'new_no_defects':
        if (!isBadBox(variant.boxCondition)) {
          newVariants.push(variant)
          newHighestVariant = getExpensiveVariant(variant, newHighestVariant)
          addVariantToSizeOptionHash(variant, sizeOptionHash)
          const noNewProductForSize = !sizeOptionHash[variantSize].new
          const newSizeOptionIsDefective =
            sizeOptionHash[variantSize].new?.shoeCondition === 'new_with_defects'
          const newSizeOptionHasBadBox = isBadBox(sizeOptionHash[variantSize].new?.boxCondition)
          if (noNewProductForSize || newSizeOptionIsDefective || newSizeOptionHasBadBox) {
            sizeOptionHash[variantSize].new = variant
          } else {
            sizeOptionHash[variantSize].new = getCheapVariant(
              sizeOptionHash[variantSize].new,
              variant,
            )
          }
        }
      // no break here bc we still want NEW_NO_DEFECTS products with bad boxes
      // to be assigned if there is no new product in the size
      // This uses a weird aspect of switch statements FALLTHROUGH!
      // https://mariusschulz.com/blog/the-curious-case-of-switch-statements-in-javascript
      case 'new_with_defects':
        addVariantToSizeOptionHash(variant, sizeOptionHash)
        if (!sizeOptionHash[variantSize].new) {
          sizeOptionHash[variantSize].new = variant
        }
        break
      case 'used':
        addVariantToSizeOptionHash(variant, sizeOptionHash)
        sizeOptionHash[variantSize].used = getCheapVariant(
          sizeOptionHash[variantSize].used,
          variant,
        )
        break
    }
  }

  // After we create the picker options we do one iteration
  // to determine the default option and lowest new / used prices
  // if enabled we also hide high priced used products
  let lowestNewOption = ''
  let lowestUsedOption = ''
  Object.keys(sizeOptionHash).forEach((optionSize) => {
    if (
      getPrice(sizeOptionHash[optionSize]?.new) < getPrice(sizeOptionHash[lowestNewOption]?.new)
    ) {
      lowestNewOption = optionSize
    }
    if (hideHighPricedUsedProduct) {
      if (
        sizeOptionHash[optionSize].new &&
        sizeOptionHash[optionSize].used &&
        getPrice(sizeOptionHash[optionSize].new) <= getPrice(sizeOptionHash[optionSize].used)
      ) {
        delete sizeOptionHash[optionSize].used
      }
    }
    if (
      getPrice(sizeOptionHash[optionSize]?.used) < getPrice(sizeOptionHash[lowestUsedOption]?.used)
    ) {
      lowestUsedOption = optionSize
    }
  })

  const sizePickerDefault =
    lowestNewOption || lowestUsedOption
      ? convertSizePickerVariantPricing(
          sizeOptionHash[lowestNewOption || lowestUsedOption],
          marketPricingEnabled,
          disambiguatedCurrencySymbol,
        )
      : undefined
  const newLowestPrice = resolveVariantPrice(
    getPriceFromVariant(sizeOptionHash[lowestNewOption]?.new, marketPricingEnabled),
    disambiguatedCurrencySymbol,
  )
  const usedLowestPrice = resolveVariantPrice(
    getPriceFromVariant(sizeOptionHash[lowestUsedOption]?.used, marketPricingEnabled),
    disambiguatedCurrencySymbol,
  )
  const newHighestPrice = resolveVariantPrice(
    getPriceFromVariant(newHighestVariant, marketPricingEnabled),
    disambiguatedCurrencySymbol,
  )

  const variantInfo: ProductVariantInfo = {
    isNewInStock: !!newVariants.length,
    newCount: newVariants.length,
    newHighestPrice,
    newLowestPrice,
    newSizes: marketPricingEnabled
      ? resolveNewSizes(newVariants, productTemplateId)
      : resolveNewSizesInstantShipOnly(newVariants, productTemplateId),
    sizePickerDefault,
    sizePickerOptions: Object.values(sizeOptionHash)
      .sort((a, b) => Number(a.size.value) - Number(b.size.value))
      .map((item) =>
        convertSizePickerVariantPricing(item, marketPricingEnabled, disambiguatedCurrencySymbol),
      ),
    usedLowestPrice,
  }

  return variantInfo
}
